import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiErr } from "@/lib/api";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { TIERS, TIER_COLOR, today } from "@/lib/perform";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Plus, TrendingUp, TrendingDown, Medal } from "lucide-react";

const GROUP_COLOR = { girls: "#FF5A5F", heroes: "#FFB340", lifts: "#CCFF00", olympic: "#B57BFF", gymnastics: "#3D7BFF", engine: "#2DD4BF" };
const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";

function Spark({ values, lower, c }) {
  if (!values || values.length < 2) return <div className="h-8" />;
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * 100},${lower ? ((v - min) / span) * 28 + 2 : 30 - ((v - min) / span) * 28}`).join(" ");
  return <svg viewBox="0 0 100 32" className="w-full h-8" preserveAspectRatio="none"><polyline points={pts} fill="none" stroke={c} strokeWidth="2" vectorEffect="non-scaling-stroke" /></svg>;
}

export function LogResult({ b, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ tier: "rx", date: today() });
  const [busy, setBusy] = useState(false);
  if (!b) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });
  const n = (v) => (v === "" || v === undefined ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/benchmarks/${b.key}/log`, { ...f, rounds: n(f.rounds), reps: n(f.reps), load_kg: n(f.load_kg), calories: n(f.calories), cap_reps: n(f.cap_reps), distance_m: n(f.distance_m) });
      toast.success(data.pr ? `New PR on ${b.name}: ${data.result.score}` : `Logged ${b.name}: ${data.result.score}`, { duration: 5000 });
      onSaved?.(); onOpenChange(false); setF({ tier: "rx", date: today() });
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  const st = b.score_type;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Log {b.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#8A91A6] -mt-2">{b.description}</p>
        <div className="space-y-3" data-testid="bench-log">
          <div className="flex flex-wrap gap-2">
            {TIERS.map((t) => <button key={t.key} onClick={() => set("tier")(t.key)} className={`rounded-full px-3 h-8 text-sm border ${f.tier === t.key ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#B7BECB]"}`}>{t.label}</button>)}
          </div>
          {st === "time" && <input className={field} placeholder="Time, e.g. 4:58" value={f.time || ""} onChange={set("time")} data-testid="bench-time" />}
          {st === "amrap" && <div className="grid grid-cols-2 gap-2"><input className={field} type="number" placeholder="Rounds" value={f.rounds || ""} onChange={set("rounds")} /><input className={field} type="number" placeholder="+ reps" value={f.reps || ""} onChange={set("reps")} /></div>}
          {st === "load" && <input className={field} type="number" step="0.5" placeholder="Heaviest single in kg" value={f.load_kg || ""} onChange={set("load_kg")} data-testid="bench-load" />}
          {(st === "reps" || st === "rounds") && <input className={field} type="number" placeholder={st === "reps" ? "Reps" : "Rounds"} value={f[st] || ""} onChange={set(st)} data-testid="bench-reps" />}
          {st === "calories" && <input className={field} type="number" placeholder="Calories" value={f.calories || ""} onChange={set("calories")} />}
          <input className={field} type="date" max={today()} value={f.date} onChange={set("date")} />
          <input className={field} placeholder="Notes (optional)" value={f.notes || ""} onChange={set("notes")} />
          <button disabled={busy} onClick={save} data-testid="bench-save" className="w-full h-11 rounded-full bg-acid text-black font-medium disabled:opacity-60">{busy ? "Saving…" : "Save result"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ bkey, open, onOpenChange }) {
  const { data } = useQuery({ queryKey: ["bench", bkey], queryFn: async () => (await api.get(`/api/benchmarks/${bkey}`)).data, enabled: !!bkey && open });
  const b = data?.benchmark;
  const c = GROUP_COLOR[b?.group] || "#CCFF00";
  const series = (data?.history || []).map((h) => ({ d: h.date?.slice(5, 10), v: b?.score_type === "amrap" ? Math.floor(h.value / 10000) + (h.value % 10000) / 100 : h.value, label: h.display }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{b?.name || "…"}</DialogTitle></DialogHeader>
        {b && (
          <div className="space-y-4">
            <p className="text-sm text-[#B7BECB]">{b.description}</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[#1E222A] p-3"><p className="mono-label">best</p><p className="font-heading font-black text-xl" style={{ color: c }}>{data.best?.display || "–"}</p></div>
              <div className="rounded-xl border border-[#1E222A] p-3"><p className="mono-label">attempts</p><p className="font-heading font-black text-xl">{data.history.length}</p></div>
              <div className="rounded-xl border border-[#1E222A] p-3"><p className="mono-label">latest</p><p className="font-heading font-black text-xl">{data.history.at(-1)?.display || "–"}</p></div>
            </div>
            {series.length >= 2 && (
              <div className="h-52 rounded-xl border border-[#1E222A] p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid stroke="#1E222A" vertical={false} />
                    <XAxis dataKey="d" tick={{ fill: "#8A91A6", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis reversed={b.lower_is_better} domain={["auto", "auto"]} tick={{ fill: "#8A91A6", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                    <Tooltip contentStyle={{ background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8 }} formatter={(v, n, p) => [p.payload.label, "result"]} />
                    <Line type="monotone" dataKey="v" stroke={c} strokeWidth={2.5} dot={{ r: 3, fill: c }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div>
              <p className="font-heading font-bold mb-2">History</p>
              {data.history.length === 0 ? <p className="text-sm text-[#8A91A6]">No results yet.</p> : (
                <div className="space-y-1.5">
                  {[...data.history].reverse().map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-sm rounded-lg border border-[#1E222A] px-3 py-2">
                      <span className="text-[#8A91A6]">{new Date(h.date).toLocaleDateString()}</span>
                      <span className="mono-label" style={{ color: TIER_COLOR[h.tier] }}>{h.tier}</span>
                      <span className="font-mono2 font-bold flex items-center gap-1">{h.is_pr && <Trophy className="w-3.5 h-3.5 text-[#FFD60A]" />}{h.display}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {data.club_board?.length > 0 && (
              <div>
                <p className="font-heading font-bold mb-2 flex items-center gap-2"><Medal className="w-4 h-4 text-acid" /> Club leaderboard</p>
                <div className="space-y-1.5">
                  {data.club_board.map((r, i) => (
                    <div key={i} className={`flex items-center gap-3 text-sm rounded-lg border px-3 py-2 ${r.mine ? "border-acid/50 bg-acid/5" : "border-[#1E222A]"}`}>
                      <span className="w-6 text-[#8A91A6]">{i + 1}</span><span className="flex-1">{r.name}</span>
                      <span className="mono-label" style={{ color: TIER_COLOR[r.tier] }}>{r.tier}</span><span className="font-mono2 font-bold">{r.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Maxes() {
  const { data } = useQuery({ queryKey: ["maxes"], queryFn: async () => (await api.get("/api/maxes")).data });
  if (!data) return <p className="mono-label animate-pulse">loading…</p>;
  const groups = Object.entries(data).filter(([, g]) => g.rows.length);
  if (!groups.length) return <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">Log a lift or benchmark and your all-time and this-year bests appear here.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="maxes">
      {groups.map(([k, g]) => (
        <div key={k} className="surface rounded-2xl p-5">
          <p className="font-heading font-bold mb-3" style={{ color: GROUP_COLOR[k] }}>{g.label}</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-2 text-sm">
            <span className="mono-label">movement</span><span className="mono-label text-right">all-time</span><span className="mono-label text-right">this year</span>
            {g.rows.map((r) => (
              <Fragment key={r.key}>
                <span>{r.name}</span>
                <span className="font-mono2 font-bold text-right">{r.all_time.display}</span>
                <span className="font-mono2 text-right text-[#8A91A6]">{r.this_year?.display || "–"}</span>
              </Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Benchmarks() {
  const qc = useQueryClient();
  const [tab, setTab] = useState(() => {
    try { const q = new URLSearchParams(window.location.search).get("tab"); if (q) return q; } catch (_) { /* ignore */ }
    return "girls";
  });
  const [log, setLog] = useState(null);
  const [detail, setDetail] = useState(null);
  const { data } = useQuery({ queryKey: ["benchmarks"], queryFn: async () => (await api.get("/api/benchmarks")).data });
  const groups = data?.groups || [];
  const g = groups.find((x) => x.key === tab);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["benchmarks"] }); qc.invalidateQueries({ queryKey: ["maxes"] }); qc.invalidateQueries({ queryKey: ["bench"] }); };
  const done = groups.reduce((n, x) => n + x.items.filter((i) => i.attempts).length, 0);
  const total = groups.reduce((n, x) => n + x.items.length, 0);
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.strength} overline="benchmarks & maxes" title="Know your numbers"
        subtitle="The Girls, the Heroes, your lifts, gymnastics and engine. Log a result, watch the trend, chase the PR."
        actions={<div className="text-right"><p className="font-heading font-black text-3xl text-acid">{done}/{total}</p><p className="mono-label">benchmarks logged</p></div>} />
      <div className="flex flex-wrap gap-2" data-testid="bench-tabs">
        {groups.map((x) => (
          <button key={x.key} onClick={() => setTab(x.key)} className={`rounded-full px-4 h-9 text-sm border ${tab === x.key ? "text-black" : "border-[#343A4A] text-[#B7BECB]"}`}
            style={tab === x.key ? { background: GROUP_COLOR[x.key], borderColor: GROUP_COLOR[x.key] } : {}}>{x.label}</button>
        ))}
        <button onClick={() => setTab("maxes")} className={`rounded-full px-4 h-9 text-sm border ${tab === "maxes" ? "bg-white text-black border-white" : "border-[#343A4A] text-[#B7BECB]"}`} data-testid="tab-maxes">My maxes</button>
      </div>
      {tab === "maxes" ? <Maxes /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {(g?.items || []).map((b, i) => {
            const c = GROUP_COLOR[b.group];
            const up = b.trend.length >= 2 && (b.lower_is_better ? b.trend.at(-1) < b.trend.at(-2) : b.trend.at(-1) > b.trend.at(-2));
            return (
              <motion.div key={b.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="relative overflow-hidden rounded-2xl border border-[#1E222A] bg-[#0F1115] p-5 group" data-testid={`bench-${b.key}`}>
                <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: c }} />
                <button onClick={() => setDetail(b.key)} className="relative text-left w-full">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-heading font-black text-xl">{b.name}</p>
                    {b.best && <span className="mono-label" style={{ color: c }}>best</span>}
                  </div>
                  <p className="text-xs text-[#8A91A6] mt-1 line-clamp-2 min-h-[2rem]">{b.description}</p>
                  <div className="flex items-end justify-between mt-3">
                    <p className="font-mono2 font-black text-2xl" style={{ color: b.best ? c : "#343A4A" }}>{b.best?.display || "not yet"}</p>
                    {b.trend.length >= 2 && (up ? <TrendingUp className="w-5 h-5 text-acid" /> : <TrendingDown className="w-5 h-5 text-[#8A91A6]" />)}
                  </div>
                  <Spark values={b.trend} lower={b.lower_is_better} c={c} />
                </button>
                <div className="relative flex items-center justify-between mt-2">
                  <span className="text-xs text-[#565C6F]">{b.attempts ? `${b.attempts} result${b.attempts > 1 ? "s" : ""}` : "no results"}</span>
                  <button onClick={() => setLog(b)} data-testid={`log-${b.key}`} className="inline-flex items-center gap-1 rounded-full px-3 h-8 text-sm border" style={{ borderColor: `${c}88`, color: c }}><Plus className="w-3.5 h-3.5" /> Log</button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <LogResult b={log} open={!!log} onOpenChange={(o) => !o && setLog(null)} onSaved={refresh} />
      <Detail bkey={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
